import type { TableAction, TableActionGroup } from "@shared/types/data-table"
import type { Invoice } from "@shared/types/invoices"
import { createBaseActions, mergeActions, type BaseActionHandlers } from "./base-actions"
import { buildGroupedRowActions } from "./build-grouped-actions"

interface InvoiceSpecificHandlers {
  onView?: (invoice: Invoice) => void
  onPreview?: (invoice: Invoice) => void
  onSendInvoice?: (invoice: Invoice) => void
  onDownloadPdf?: (invoice: Invoice) => void
  onMarkAsPaid?: (invoice: Invoice) => void
  onMarkAsDraft?: (invoice: Invoice) => void
  onDuplicateInvoice?: (invoice: Invoice) => void
  onEditDraft?: (invoice: Invoice) => void
  onCancelInvoice?: (invoice: Invoice) => void
  onRecordPayment?: (invoice: Invoice) => void
  onViewCustomer?: (invoice: Invoice) => void
  onViewPayment?: (invoice: Invoice) => void
  onViewSubscription?: (invoice: Invoice) => void
  onBulkSendInvoices?: (invoiceIds: string[]) => void
  onBulkMarkAsPaid?: (invoiceIds: string[]) => void
  onBulkDownloadPdfs?: (invoiceIds: string[]) => void
  onFlag?: (invoice: Invoice) => void
  onAddNote?: (invoice: Invoice) => void
  onCopyInvoiceId?: (invoice: Invoice) => void
}

export type InvoiceActionHandlers = BaseActionHandlers<Invoice> & InvoiceSpecificHandlers

// Row actions for tables - quick access operations
const createInvoiceRowActions = (handlers: InvoiceActionHandlers) => [
  {
    key: "sendInvoice",
    label: "Send invoice",
    onClick: (invoice: Invoice) => handlers.onSendInvoice?.(invoice),
    condition: (invoice: Invoice) => ["draft", "sent"].includes(invoice.status),
  },
  {
    key: "downloadPdf",
    label: "Download PDF",
    onClick: (invoice: Invoice) => handlers.onDownloadPdf?.(invoice),
  },
  {
    key: "copyId",
    label: "Copy invoice ID",
    onClick: (invoice: Invoice) => handlers.onCopyInvoiceId?.(invoice),
  },
]

// Panel actions for detail pages - comprehensive operations
const createInvoicePanelActions = (handlers: InvoiceActionHandlers) => [
  {
    key: "editDraft",
    label: "Edit draft",
    onClick: (invoice: Invoice) => handlers.onEditDraft?.(invoice),
    condition: (invoice: Invoice) => invoice.status === "draft",
  },
  {
    key: "sendInvoice",
    label: "Send invoice",
    onClick: (invoice: Invoice) => handlers.onSendInvoice?.(invoice),
    condition: (invoice: Invoice) => ["draft", "sent"].includes(invoice.status),
  },
  {
    key: "recordPayment",
    label: "Record payment",
    onClick: (invoice: Invoice) => handlers.onRecordPayment?.(invoice),
    condition: (invoice: Invoice) => ["sent", "overdue"].includes(invoice.status),
  },
  {
    key: "downloadPdf",
    label: "Download PDF",
    onClick: (invoice: Invoice) => handlers.onDownloadPdf?.(invoice),
  },
  {
    key: "markAsPaid",
    label: "Mark as paid",
    onClick: (invoice: Invoice) => handlers.onMarkAsPaid?.(invoice),
    condition: (invoice: Invoice) => ["sent", "overdue"].includes(invoice.status),
  },
  {
    key: "markAsDraft",
    label: "Mark as draft",
    onClick: (invoice: Invoice) => handlers.onMarkAsDraft?.(invoice),
    condition: (invoice: Invoice) => invoice.status === "sent",
  },
  {
    key: "duplicateInvoice",
    label: "Duplicate invoice",
    onClick: (invoice: Invoice) => handlers.onDuplicateInvoice?.(invoice),
  },
  {
    key: "viewCustomer",
    label: "View customer",
    onClick: (invoice: Invoice) => handlers.onViewCustomer?.(invoice),
    condition: (invoice: Invoice) => !!invoice.customerId,
  },
  {
    key: "viewPayment",
    label: "View payment",
    onClick: (invoice: Invoice) => handlers.onViewPayment?.(invoice),
    condition: (invoice: Invoice) => !!invoice.paymentId,
  },
  {
    key: "viewSubscription",
    label: "View subscription",
    onClick: (invoice: Invoice) => handlers.onViewSubscription?.(invoice),
    condition: (invoice: Invoice) => !!invoice.subscriptionId,
  },
  {
    key: "flag",
    label: "Flag",
    onClick: (invoice: Invoice) => handlers.onFlag?.(invoice),
    variant: "outline" as const,
  },
  {
    key: "addNote",
    label: "Add note",
    onClick: (invoice: Invoice) => handlers.onAddNote?.(invoice),
  },
  {
    key: "copyId",
    label: "Copy invoice ID",
    onClick: (invoice: Invoice) => handlers.onCopyInvoiceId?.(invoice),
  },
  {
    key: "cancelInvoice",
    label: "Cancel invoice",
    onClick: (invoice: Invoice) => handlers.onCancelInvoice?.(invoice),
    condition: (invoice: Invoice) => !["cancelled", "paid"].includes(invoice.status),
    variant: "destructive" as const,
  },
]

export const getInvoiceActions = (handlers: InvoiceActionHandlers = {}) => {
  const baseActions = createBaseActions('Invoice', handlers, {
    showEdit: true,
    showDelete: true,
    showFlag: false,
    showAddNote: false,
    showCopyId: false,
    showExport: true,
  })

  return mergeActions(baseActions, {
    bulkActions: [
      {
        key: "sendInvoices",
        label: "Send Invoices",
        onClick: (ids: string[]) => handlers.onBulkSendInvoices?.(ids),
        disabled: (ids: string[]) => ids.length === 0,
      },
      {
        key: "markAsPaid",
        label: "Mark as Paid",
        onClick: (ids: string[]) => handlers.onBulkMarkAsPaid?.(ids),
        disabled: (ids: string[]) => ids.length === 0,
      },
      {
        key: "downloadPdfs",
        label: "Download PDFs",
        onClick: (ids: string[]) => handlers.onBulkDownloadPdfs?.(ids),
        disabled: (ids: string[]) => ids.length === 0,
      },
    ],
    rowActions: createInvoiceRowActions(handlers),
  })
}

export const getInvoiceBulkActions = (handlers: InvoiceActionHandlers = {}) =>
  getInvoiceActions(handlers).bulkActions

export const getInvoiceRowActions = (handlers: InvoiceActionHandlers = {}): TableActionGroup<Invoice>[] =>
  buildGroupedRowActions<Invoice>({
    view: [
      {
        key: "preview",
        label: "Preview",
        onClick: (invoice) => handlers.onPreview?.(invoice),
      },
    ],
    actions: [
      {
        key: "edit",
        label: "Edit draft",
        onClick: (invoice) => handlers.onEditDraft?.(invoice),
        condition: (invoice) => invoice.status === "draft",
      },
      {
        key: "sendInvoice",
        label: "Send invoice",
        onClick: (invoice) => handlers.onSendInvoice?.(invoice),
        condition: (invoice) => ["draft", "sent"].includes(invoice.status),
      },
      {
        key: "recordPayment",
        label: "Record payment",
        onClick: (invoice) => handlers.onRecordPayment?.(invoice),
        condition: (invoice) => ["sent", "overdue"].includes(invoice.status),
      },
      {
        key: "downloadPdf",
        label: "Download PDF",
        onClick: (invoice) => handlers.onDownloadPdf?.(invoice),
      },
      {
        key: "markAsPaid",
        label: "Mark as paid",
        onClick: (invoice) => handlers.onMarkAsPaid?.(invoice),
        condition: (invoice) => ["sent", "overdue"].includes(invoice.status),
      },
      {
        key: "markAsDraft",
        label: "Mark as draft",
        onClick: (invoice) => handlers.onMarkAsDraft?.(invoice),
        condition: (invoice) => invoice.status === "sent",
      },
      {
        key: "duplicateInvoice",
        label: "Duplicate invoice",
        onClick: (invoice) => handlers.onDuplicateInvoice?.(invoice),
      },
      {
        key: "copyId",
        label: "Copy invoice ID",
        onClick: (invoice) => handlers.onCopyInvoiceId?.(invoice),
      },
      {
        key: "cancelInvoice",
        label: "Cancel invoice",
        onClick: (invoice) => handlers.onCancelInvoice?.(invoice),
        condition: (invoice) => !["cancelled", "paid"].includes(invoice.status),
        variant: "destructive" as const,
      },
    ],
    links: [
      {
        key: "viewCustomer",
        label: "View customer",
        onClick: (invoice) => handlers.onViewCustomer?.(invoice),
        condition: (invoice) => !!invoice.customerId,
      },
      {
        key: "viewPayment",
        label: "View payment",
        onClick: (invoice) => handlers.onViewPayment?.(invoice),
        condition: (invoice) => !!invoice.paymentId,
      },
      {
        key: "viewSubscription",
        label: "View subscription",
        onClick: (invoice) => handlers.onViewSubscription?.(invoice),
        condition: (invoice) => !!invoice.subscriptionId,
      },
    ],
  })

export const getInvoicePanelActions = (handlers: InvoiceActionHandlers = {}) => {
  const baseActions = createBaseActions('Invoice', handlers, {
    showEdit: true,
    showDelete: true,
    showFlag: false,
    showAddNote: false,
    showCopyId: false,
    showExport: true,
  })

  return mergeActions(baseActions, {
    rowActions: createInvoicePanelActions(handlers),
  }).rowActions
}

export const getInvoiceHoverActions = (handlers: InvoiceActionHandlers = {}): TableAction<Invoice>[] => [
  {
    key: "view",
    label: "View",
    onClick: (invoice: Invoice) => handlers.onView?.(invoice),
  },
  {
    key: "downloadPdf",
    label: "Download PDF",
    onClick: (invoice: Invoice) => handlers.onDownloadPdf?.(invoice),
  },
]
